"use client";

import React from "react";
import { Upload, ClipboardCheck, ShieldCheck, Quote, ArrowRight, Play, Layers3 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
const steps = [{
  number: "01",
  title: "Upload company knowledge",
  description: "Bring documents into EloRAG and keep them scoped to the right tenant or client environment.",
  icon: Upload
}, {
  number: "02",
  title: "Review what the AI may use",
  description: "Your team validates content before it becomes available to the retrieval layer.",
  icon: ClipboardCheck
}, {
  number: "03",
  title: "Approve content for retrieval",
  description: "Only approved content is indexed and made available for grounded AI answers.",
  icon: ShieldCheck
}, {
  number: "04",
  title: "Answer with citations",
  description: "EloRAG returns citation-based answers and refuses when evidence is insufficient.",
  icon: Quote
}];
export default function SeeHowEloragWorks() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: cardsRef,
    isVisible: isCardsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: bottomRef,
    isVisible: isBottomVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="see-how-elorag-works" className="overflow-hidden bg-background py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerRef} className={cn("mx-auto mb-12 max-w-3xl text-center transition-all duration-700 ease-out", isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
            <Layers3 className="size-4" />
            <span>From Documents to Grounded Answers</span>
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            From approved documents to grounded answers
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
            EloRAG gives businesses and agencies a controlled RAG workflow for company knowledge —
            without having to build their own retrieval and governance stack from scratch.
          </p>
        </div>

        <div ref={cardsRef} className={cn("grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4 transition-all duration-700 ease-out", isCardsVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0")}>
          {steps.map((step, i) => {
          const Icon = step.icon;
          return <Card key={step.number} className="relative overflow-hidden rounded-2xl border border-border/70 bg-card p-6 shadow-[0_12px_30px_-15px_rgba(0,0,0,0.12)] transition-all duration-300 hover:border-primary/25 hover:shadow-[0_18px_40px_-18px_rgba(0,0,0,0.15)]" style={{
            transitionDelay: `${i * 100}ms`
          }}>
                <div className="mb-6 flex items-center justify-between">
                  <div className="rounded-full border border-border/70 bg-background px-3 py-1 text-xs font-semibold tracking-wider text-muted-foreground">
                    {step.number}
                  </div>
                  <div className="rounded-lg bg-primary/10 p-3">
                    <Icon className="size-5 text-primary" />
                  </div>
                </div>

                <h3 className="mb-3 text-xl font-semibold text-foreground">{step.title}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {step.description}
                </p>
              </Card>;
        })}
        </div>

        <div ref={bottomRef} className={cn("mt-12 rounded-2xl border border-border/70 bg-secondary/35 p-8 shadow-[0_12px_30px_-15px_rgba(0,0,0,0.12)] transition-all duration-700 ease-out lg:p-10", isBottomVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0")}>
          <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <h3 className="text-2xl font-bold tracking-tight text-foreground">
                A controlled RAG workflow, without building it yourself
              </h3>
              <p className="mt-3 text-lg leading-relaxed text-muted-foreground">
                EloRAG combines approved-only retrieval, citation-based answering, refusal logic,
                and tenant-aware delivery in one deployable platform for business and agency use.
              </p>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row lg:flex-col xl:flex-row">
              <Dialog>
                <DialogTrigger asChild>
                  <Button size="lg" className="shadow-sm">
                    <Play className="mr-2 size-4 fill-current" />
                    Watch Demo
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-3xl">
                  <DialogHeader>
                    <DialogTitle>EloRAG Platform Overview</DialogTitle>
                    <DialogDescription>
                      See how EloRAG handles approved-only retrieval, governed workflows, and
                      citation-based answering in practice.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="flex aspect-video w-full items-center justify-center rounded-md border border-border bg-muted">
                    <div className="flex flex-col items-center gap-4 text-muted-foreground">
                      <Play className="size-12 opacity-50" />
                      <p>Video Player Placeholder</p>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              <Button size="lg" variant="outline" asChild>
                <Link to="/BookADemoContactSalesPage">
                  Book a Demo
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}